import { useEffect, useMemo, useState } from "react";
import { AdminShell } from "@/components/admin/AdminShell";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toCsv, downloadCsv } from "@/lib/csv";
import { toast } from "sonner";
import { Trash2, Flag, Plus } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

type Row = {
  id: string;
  reason: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  service: string | null;
  message: string | null;
  ip: string | null;
  user_agent: string | null;
  recaptcha_score: number | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
};

export default function AdminSpam() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [reasonFilter, setReasonFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [keywords, setKeywords] = useState<Array<{ id: string; pattern: string; enabled: boolean; source: string; hit_count: number; created_at: string }>>([]);
  const [reportingRow, setReportingRow] = useState<Row | null>(null);
  const [suggested, setSuggested] = useState<{ token: string; selected: boolean }[]>([]);
  const [customPattern, setCustomPattern] = useState("");
  const [newKeyword, setNewKeyword] = useState("");

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("blocked_submissions")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(500);
    setLoading(false);
    if (error) { toast.error(error.message); return; }
    setRows((data ?? []) as Row[]);
  };

  const loadKeywords = async () => {
    const { data, error } = await supabase
      .from("spam_keywords")
      .select("id,pattern,enabled,source,hit_count,created_at")
      .order("created_at", { ascending: false });
    if (error) { toast.error(error.message); return; }
    setKeywords((data ?? []) as any);
  };

  useEffect(() => { load(); loadKeywords(); }, []);

  // Extract candidate keywords from a message: notable 4+ char tokens, capped.
  const extractTokens = (text: string): string[] => {
    const stop = new Set(["this","that","with","from","have","your","will","just","like","want","need","know","what","when","where","there","their","they","them","were","been","more","most","some","also","about","would","could","should","really","please","thanks","thank","hello","name","email","https","http","www","com","net","org"]);
    const seen = new Set<string>();
    const out: string[] = [];
    for (const raw of (text || "").toLowerCase().split(/[^a-z0-9$]+/)) {
      if (raw.length < 4 || raw.length > 30) continue;
      if (stop.has(raw)) continue;
      if (/^\d+$/.test(raw)) continue;
      if (seen.has(raw)) continue;
      seen.add(raw);
      out.push(raw);
      if (out.length >= 8) break;
    }
    return out;
  };

  const openReport = (row: Row) => {
    setReportingRow(row);
    setSuggested(extractTokens(`${row.message ?? ""} ${row.full_name ?? ""} ${row.service ?? ""}`).map(token => ({ token, selected: false })));
    setCustomPattern("");
  };

  const submitReport = async () => {
    if (!reportingRow) return;
    const picked = suggested.filter(s => s.selected).map(s => s.token);
    if (customPattern.trim()) picked.push(customPattern.trim());
    if (!picked.length) { toast.error("Pick at least one keyword to block."); return; }
    const inserts = picked.map(p => ({ pattern: p, source: "reported", is_regex: false }));
    const { error } = await supabase.from("spam_keywords").upsert(inserts, { onConflict: "pattern" });
    if (error) { toast.error(error.message); return; }
    toast.success(`Added ${picked.length} keyword${picked.length === 1 ? "" : "s"} to denylist.`);
    setReportingRow(null);
    loadKeywords();
  };

  const addManualKeyword = async () => {
    const p = newKeyword.trim();
    if (!p) return;
    const { error } = await supabase.from("spam_keywords").upsert({ pattern: p, source: "manual", is_regex: false }, { onConflict: "pattern" });
    if (error) { toast.error(error.message); return; }
    setNewKeyword("");
    loadKeywords();
  };

  const toggleKeyword = async (id: string, enabled: boolean) => {
    const { error } = await supabase.from("spam_keywords").update({ enabled }).eq("id", id);
    if (error) { toast.error(error.message); return; }
    loadKeywords();
  };

  const deleteKeyword = async (id: string) => {
    if (!confirm("Remove this keyword from the denylist?")) return;
    const { error } = await supabase.from("spam_keywords").delete().eq("id", id);
    if (error) { toast.error(error.message); return; }
    loadKeywords();
  };

  const reasonOptions = useMemo(() => {
    const set = new Set<string>();
    rows.forEach(r => set.add(r.reason.split(":")[0]));
    return Array.from(set).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    return rows.filter(r => {
      if (reasonFilter !== "all" && !r.reason.startsWith(reasonFilter)) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = `${r.email ?? ""} ${r.full_name ?? ""} ${r.message ?? ""} ${r.ip ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, reasonFilter, search]);

  const exportCsv = () => {
    const csv = toCsv(filtered.map(r => ({
      created_at: r.created_at,
      reason: r.reason,
      full_name: r.full_name,
      email: r.email,
      phone: r.phone,
      service: r.service,
      ip: r.ip,
      recaptcha_score: r.recaptcha_score,
      user_agent: r.user_agent,
      message: r.message,
    })));
    downloadCsv(`blocked-submissions-${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  const deleteRow = async (id: string) => {
    if (!confirm("Delete this blocked submission?")) return;
    const { error } = await supabase.from("blocked_submissions").delete().eq("id", id);
    if (error) { toast.error(error.message); return; }
    setRows(rs => rs.filter(r => r.id !== id));
  };

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    rows.forEach(r => { const k = r.reason.split(":")[0]; c[k] = (c[k] ?? 0) + 1; });
    return c;
  }, [rows]);

  return (
    <AdminShell
      title="Spam Review"
      actions={
        <>
          <Button variant="outline" onClick={load} disabled={loading}>
            {loading ? "Loading…" : "Refresh"}
          </Button>
          <Button onClick={exportCsv} disabled={!filtered.length}>Export CSV</Button>
        </>
      }
    >
      <Tabs defaultValue="submissions" className="space-y-6">
        <TabsList>
          <TabsTrigger value="submissions">Blocked submissions</TabsTrigger>
          <TabsTrigger value="keywords">Denylist ({keywords.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="submissions" className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {["honeypot", "rate_limit", "recaptcha_low_score", "recaptcha_failed", "keyword"].map(k => (
            <div key={k} className="border border-border bg-panel p-3">
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-mono">{k}</div>
              <div className="text-2xl font-display">{counts[k] ?? 0}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <Input
            placeholder="Search email, name, message, IP…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="max-w-sm"
          />
          <Select value={reasonFilter} onValueChange={setReasonFilter}>
            <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All reasons</SelectItem>
              {reasonOptions.map(r => (
                <SelectItem key={r} value={r}>{r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-sm text-muted-foreground font-mono">
            {filtered.length} / {rows.length}
          </div>
        </div>

        <div className="border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-panel">
              <tr className="text-left font-mono text-xs uppercase tracking-wider text-muted-foreground">
                <th className="p-3">When</th>
                <th className="p-3">Reason</th>
                <th className="p-3">From</th>
                <th className="p-3">Message</th>
                <th className="p-3">IP</th>
                <th className="p-3">Score</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} className="border-t border-border align-top">
                  <td className="p-3 whitespace-nowrap text-muted-foreground font-mono text-xs">
                    {new Date(r.created_at).toLocaleString()}
                  </td>
                  <td className="p-3 font-mono text-xs">
                    <span className="px-2 py-0.5 bg-primary/10 text-primary">{r.reason}</span>
                  </td>
                  <td className="p-3">
                    <div className="font-medium">{r.full_name || "—"}</div>
                    <div className="text-muted-foreground text-xs">{r.email}</div>
                    {r.phone && <div className="text-muted-foreground text-xs">{r.phone}</div>}
                  </td>
                  <td className="p-3 max-w-md">
                    <div className="line-clamp-3 whitespace-pre-wrap">{r.message || "—"}</div>
                  </td>
                  <td className="p-3 font-mono text-xs text-muted-foreground">{r.ip}</td>
                  <td className="p-3 font-mono text-xs">
                    {r.recaptcha_score == null ? "—" : r.recaptcha_score.toFixed(2)}
                  </td>
                  <td className="p-3 space-x-2 whitespace-nowrap">
                    <button onClick={() => openReport(r)} className="text-muted-foreground hover:text-primary" aria-label="Report spam" title="Report — add keywords to denylist">
                      <Flag className="w-4 h-4 inline" />
                    </button>
                    <button onClick={() => deleteRow(r.id)} className="text-muted-foreground hover:text-destructive" aria-label="Delete">
                      <Trash2 className="w-4 h-4 inline" />
                    </button>
                  </td>
                </tr>
              ))}
              {!filtered.length && (
                <tr><td colSpan={7} className="p-8 text-center text-muted-foreground font-mono text-sm">
                  {loading ? "Loading…" : "No blocked submissions."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        </TabsContent>

        <TabsContent value="keywords" className="space-y-4">
          <div className="flex gap-2 items-center">
            <Input
              placeholder="Add keyword or phrase (e.g. crypto airdrop)"
              value={newKeyword}
              onChange={e => setNewKeyword(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addManualKeyword(); } }}
              className="max-w-md"
            />
            <Button onClick={addManualKeyword}><Plus className="w-4 h-4 mr-1" />Add</Button>
          </div>
          <div className="border border-border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-panel">
                <tr className="text-left font-mono text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="p-3">Pattern</th>
                  <th className="p-3">Source</th>
                  <th className="p-3">Hits</th>
                  <th className="p-3">Enabled</th>
                  <th className="p-3">Added</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {keywords.map(k => (
                  <tr key={k.id} className="border-t border-border">
                    <td className="p-3 font-mono">{k.pattern}</td>
                    <td className="p-3 text-xs text-muted-foreground">{k.source}</td>
                    <td className="p-3 font-mono text-xs">{k.hit_count}</td>
                    <td className="p-3"><Checkbox checked={k.enabled} onCheckedChange={(v) => toggleKeyword(k.id, !!v)} /></td>
                    <td className="p-3 text-xs text-muted-foreground font-mono">{new Date(k.created_at).toLocaleDateString()}</td>
                    <td className="p-3">
                      <button onClick={() => deleteKeyword(k.id)} className="text-muted-foreground hover:text-destructive" aria-label="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {!keywords.length && (
                  <tr><td colSpan={6} className="p-8 text-center text-muted-foreground font-mono text-sm">No custom keywords yet. Use "Report spam" on a blocked submission to add some automatically.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!reportingRow} onOpenChange={(o) => !o && setReportingRow(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Report as spam</DialogTitle>
            <DialogDescription>
              Pick suggested keywords to add to the denylist, or type a custom phrase. Future submissions containing any of these will be auto-blocked.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-mono mb-2">Suggested keywords</div>
              {suggested.length ? (
                <div className="flex flex-wrap gap-2">
                  {suggested.map((s, i) => (
                    <label key={s.token} className={`px-3 py-1.5 border cursor-pointer text-sm font-mono ${s.selected ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={s.selected}
                        onChange={e => setSuggested(arr => arr.map((x, idx) => idx === i ? { ...x, selected: e.target.checked } : x))}
                      />
                      {s.token}
                    </label>
                  ))}
                </div>
              ) : <p className="text-sm text-muted-foreground">No keyword candidates found in the message.</p>}
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-mono mb-2">Custom phrase</div>
              <Input
                placeholder="e.g. crypto airdrop"
                value={customPattern}
                onChange={e => setCustomPattern(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportingRow(null)}>Cancel</Button>
            <Button onClick={submitReport}>Add to denylist</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}